import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { requireAuthentication, checkAuthWithTimeout } from '@/utils/auth'
import HomeView from '../views/HomeView.vue'

// Redirect authenticated users from home to app
const redirectIfAuthenticated = async (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const userStore = useUserStore()

  try {
    const isAuthenticated = await checkAuthWithTimeout(userStore, {
      logPrefix: 'Home redirect check'
    })

    if (isAuthenticated) {
      console.log('🏠→📱 User authenticated, redirecting to /app')
      next({ path: '/app', replace: true })
    } else {
      next()
    }
  } catch (error) {
    console.error('Home redirect check failed:', error)
    next()
  }
}

// Simplified authentication guard using shared utility
const requireAuth = async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  const userStore = useUserStore()

  console.log('🔐 Auth guard - route:', to.path, 'initialized:', userStore.initialized, 'authenticated:', userStore.isAuthenticated)

  try {
    const result = await requireAuthentication(userStore, to.path)

    if (result.success) {
      next()
    } else if (result.shouldRedirect) {
      next({ path: result.redirectPath || '/', replace: true })
    } else {
      next()
    }
  } catch (error) {
    console.error('❌ Auth guard error:', error)
    next({ path: '/', replace: true })
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        title: 'PopMates - Discover Places Together',
        requiresAuth: false,
        isPublic: true
      }
      // No beforeEnter - page renders immediately, auth redirect handled in HomeView
    },
    {
      path: '/app',
      name: 'mainApp',
      // Dynamic import for code splitting
      component: () => import('../views/MainAppView.vue'),
      meta: {
        title: 'PopMates App',
        requiresAuth: true,
        isPublic: false
      },
      beforeEnter: requireAuth
    },
    {
      path: '/auth/callback',
      name: 'authCallback',
      component: () => import('../views/AuthCallbackView.vue'),
      meta: {
        title: 'Authenticating - PopMates',
        requiresAuth: false,
        isPublic: true
      }
    },
    {
      path: '/forgot-password',
      name: 'forgotPassword',
      component: () => import('../views/ForgotPasswordView.vue'),
      meta: {
        title: 'Forgot Password - PopMates',
        requiresAuth: false,
        isPublic: true
      }
    },
    {
      path: '/reset-password',
      name: 'resetPassword',
      component: () => import('../views/ResetPasswordView.vue'),
      meta: {
        title: 'Reset Password - PopMates',
        requiresAuth: false,
        isPublic: true
      }
    },
    {
      path: '/profile/:userId',
      name: 'profile',
      // Dynamic import for code splitting
      component: () => import('../views/ProfileView.vue'),
      meta: {
        title: 'User Profile - PopMates',
        requiresAuth: true,
        isPublic: false
      },
      beforeEnter: requireAuth,
      props: true // Pass route params as props
    },
    {
      path: '/about',
      name: 'about',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/AboutView.vue'),
      meta: {
        title: 'About - PopMates',
        requiresAuth: false,
        isPublic: true
      }
    },
    // Development-only routes
    ...(import.meta.env.DEV ? [{
      path: '/test/geoapify',
      name: 'geoapifyTest',
      component: () => import('../views/GeoapifyTestView.vue'),
      meta: {
        title: 'Geoapify API Test - PopMates',
        requiresAuth: false,
        isPublic: true
      }
    }] : []),
    {
      // Catch-all 404 route
      path: '/:pathMatch(.*)*',
      name: 'notFound',
      redirect: '/'
    }
  ],
})

// Global navigation guards - simplified with shared utility
router.beforeEach(async (to, _from, next) => {
  const userStore = useUserStore()

  // Set page title from route meta
  if (to.meta.title) {
    document.title = to.meta.title as string
  }

  // Handle authentication requirement
  if (to.meta.requiresAuth) {
    console.log('🔐 Global guard - route requires auth:', to.path)

    // Skip auth check for routes that already have beforeEnter guards
    if (to.name === 'mainApp' || to.name === 'profile') {
      console.log('📝 Route has beforeEnter guard, skipping global auth check')
      next()
      return
    }

    try {
      const result = await requireAuthentication(userStore, to.path, {
        logPrefix: `Global guard (${to.path})`
      })

      if (result.success) {
        next()
      } else if (result.shouldRedirect) {
        next({ path: result.redirectPath || '/', replace: true })
      } else {
        next()
      }
    } catch (error) {
      console.error('❌ Global navigation guard error:', error)
      next({ path: '/', replace: true })
    }
  } else {
    // Route doesn't require authentication
    console.log('📝 Route does not require auth:', to.path)
    next()
  }
})

// Navigation error handling with chunk loading recovery
router.onError((error) => {
  console.error('Router error:', error)

  // Handle chunk loading errors (typically caused by outdated service worker cache)
  if (error.message && error.message.includes('Failed to fetch dynamically imported module')) {
    // Check if we've already attempted a reload to prevent infinite loop
    const hasReloaded = sessionStorage.getItem('chunk_error_reloaded')

    if (hasReloaded) {
      console.error('❌ Chunk loading failed even after reload. Possible persistent issue.')
      // Clear the reload flag and redirect flags to reset state
      sessionStorage.removeItem('chunk_error_reloaded')
      sessionStorage.removeItem('chunk_error_redirect')
      // Don't reload again - let the app show an error state or redirect to home
      return
    }

    console.warn('⚠️ Chunk loading error detected - likely stale cache. Reloading page...')

    // Store the attempted path to redirect after reload
    const targetPath = router.currentRoute.value.fullPath
    sessionStorage.setItem('chunk_error_redirect', targetPath)

    // Set flag to prevent infinite reload loop
    sessionStorage.setItem('chunk_error_reloaded', 'true')

    // Force reload to get fresh chunk references
    // This will bypass the service worker cache
    window.location.reload()
  }
})

// Check if we reloaded due to chunk error and redirect to intended path
if (typeof window !== 'undefined') {
  const redirectPath = sessionStorage.getItem('chunk_error_redirect')
  if (redirectPath) {
    sessionStorage.removeItem('chunk_error_redirect')

    // Wait for router to be ready and chunks to load before clearing the guard flag
    const clearReloadFlag = () => {
      router.isReady().then(() => {
        // Clear the reload flag only after router confirms successful navigation
        sessionStorage.removeItem('chunk_error_reloaded')
      }).catch((err) => {
        console.error('Router ready check failed:', err)
        // Keep the flag intact if router is not ready
      })
    }

    // Only redirect if we're not already there
    if (window.location.pathname !== redirectPath) {
      setTimeout(() => {
        router.push(redirectPath)
          .then(() => {
            // Clear flag after successful navigation and router is ready
            clearReloadFlag()
          })
          .catch((err) => {
            console.error('Error redirecting after chunk reload:', err)
            // Keep the flag intact on navigation failure to prevent reload loop
          })
      }, 100)
    } else {
      // Already at target path, but still wait for router to confirm chunks loaded
      clearReloadFlag()
    }
  }
}

// Helper functions for programmatic navigation
export const navigationHelpers = {
  // Redirect to main app after successful login
  goToApp() {
    router.push('/app')
  },
  
  // Redirect to home after logout
  goToHome() {
    router.push('/')
  },
  
  // Go to user profile
  goToProfile(userId: string) {
    router.push(`/profile/${userId}`)
  },
  
  // Go back to previous page or fallback
  goBack(fallback: string = '/') {
    if (window.history.length > 1) {
      router.back()
    } else {
      router.push(fallback)
    }
  },
  
  // Check if current route requires authentication
  isAuthRequiredRoute() {
    return router.currentRoute.value.meta.requiresAuth === true
  },
  
  // Get current route name
  getCurrentRouteName() {
    return router.currentRoute.value.name
  }
}

export default router
